import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const URGENCY_COLORS = {
  'Critical': 'bg-red-100 text-red-700 border-red-200',
  'High':     'bg-orange-100 text-orange-700 border-orange-200',
  'Medium':   'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const EFFORT_COLORS = {
  'Low':    'bg-green-100 text-green-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'High':   'bg-purple-100 text-purple-700',
};

const MATURITY_COLORS = {
  'Initial':    { bar: 'bg-red-500',    text: 'text-red-600',    bg: 'bg-red-50'    },
  'Developing': { bar: 'bg-orange-500', text: 'text-orange-600', bg: 'bg-orange-50' },
  'Defined':    { bar: 'bg-yellow-500', text: 'text-yellow-600', bg: 'bg-yellow-50' },
  'Managed':    { bar: 'bg-blue-500',   text: 'text-blue-600',   bg: 'bg-blue-50'   },
  'Optimised':  { bar: 'bg-green-500',  text: 'text-green-600',  bg: 'bg-green-50'  },
  'Optimized':  { bar: 'bg-green-500',  text: 'text-green-600',  bg: 'bg-green-50'  }, 
};

const DIM_LABELS = {
  data_privacy:          'Data Privacy',
  regulatory_compliance: 'Regulatory Compliance',
  security_posture:      'Security Posture',
  vendor_stability:      'Vendor Stability',
  shadow_ai_risk:        'Shadow AI Risk',
  business_impact:       'Business Impact',
};

const formatCurrency = (val, country) => {
  if (!val) return '—';
  
  const mappings = {
    'India': { currency: 'INR', locale: 'en-IN' },
    'Singapore': { currency: 'SGD', locale: 'en-SG' },
    'Australia': { currency: 'AUD', locale: 'en-AU' },
    'New Zealand': { currency: 'NZD', locale: 'en-NZ' },
    'Japan': { currency: 'JPY', locale: 'ja-JP' },
    'South Korea': { currency: 'KRW', locale: 'ko-KR' },
    'Hong Kong': { currency: 'HKD', locale: 'en-HK' },
    'United States': { currency: 'USD', locale: 'en-US' },
    'Canada': { currency: 'CAD', locale: 'en-CA' },
    'United Kingdom': { currency: 'GBP', locale: 'en-GB' },
    'Sweden': { currency: 'SEK', locale: 'sv-SE' },
    'Switzerland': { currency: 'CHF', locale: 'de-CH' },
    'United Arab Emirates': { currency: 'AED', locale: 'en-AE' },
    'Saudi Arabia': { currency: 'SAR', locale: 'en-SA' },
    'South Africa': { currency: 'ZAR', locale: 'en-ZA' }
  };

  const euCountries = [
    'Germany', 'France', 'Netherlands', 'Ireland', 
    'Austria', 'Belgium', 'Cyprus', 'Estonia', 'Finland', 'Greece',
    'Italy', 'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Portugal',
    'Slovakia', 'Slovenia', 'Spain'
  ];

  let currency = 'USD'; 
  let locale = 'en-US';

  if (mappings[country]) {
    currency = mappings[country].currency;
    locale = mappings[country].locale;
  } else if (euCountries.includes(country)) {
    currency = 'EUR';
    locale = 'en-IE';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    notation: "compact",
    maximumFractionDigits: 1
  }).format(val);
};

export default function RiskAnalysis() {
  const { user, token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  
  const [inventory, setInventory]       = useState([]);
  const [analysis, setAnalysis]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [running, setRunning]           = useState(false);
  const [error, setError]               = useState('');
  const [activeTab, setActiveTab]       = useState('overview');
  const [expandedTool, setExpandedTool] = useState(null);
  const [progress, setProgress]         = useState({});
  
  const [showEvidenceModal, setShowEvidenceModal] = useState(null);
  const [evidenceForm, setEvidenceForm] = useState({ evidence: '', notes: '', status: 'Completed', fileBase64: null, fileName: '' });
  const [savingProgress, setSavingProgress] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${baseURL}/api/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const map = {};
      res.data.forEach(item => { map[item.item_key || item.item_title] = item; });
      setProgress(map);
    } catch (err) {
      console.error('Failed to fetch progress');
    }
  };

  const fetchData = async () => {
    if (!user || !user.org_id) return;

    try {

      // Pass orgId as query parameters to prevent 400 Bad Requests
      const reqConfig = { headers: { Authorization: `Bearer ${token}` }, params: { orgId: user.org_id } };
      const [invRes, anaRes] = await Promise.all([
        axios.get(`${baseURL}/api/inventory`, reqConfig),
        axios.get(`${baseURL}/api/inventory/analyze`, reqConfig),
      ]);

      setInventory(invRes.data);
      setAnalysis(anaRes.data);
      await fetchProgress();
    } catch (err) {
      console.error('Failed to fetch data');
    }
    setLoading(false);
  };

  const handleMarkDone = (itemKey, itemTitle, itemType, toolId = null) => {
    const prog = progress[itemKey] || { status: 'Pending' };
    setShowEvidenceModal({ itemKey, itemTitle, itemType, toolId });
    setEvidenceForm({ 
      status: prog.status !== 'Pending' ? prog.status : 'Completed', 
      evidence: prog.evidence || '', 
      notes: prog.notes || '',
      fileBase64: null,
      fileName: ''
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (user?.tier === 0) {
      alert('Secure evidence uploads are a premium feature unlocked at Tier 1.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setEvidenceForm(prev => ({ ...prev, fileBase64: reader.result, fileName: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const saveProgress = async (itemKey, itemTitle, itemType, status, toolId = null) => {
    try {
      const payload = {
        item_type: itemType,
        tool_id: toolId,
        item_key: itemKey,
        item_title: itemTitle,
        status,
        evidence: evidenceForm.evidence,
        notes: evidenceForm.notes,
      };

      if (evidenceForm.fileBase64) {
        payload.evidence_base64 = evidenceForm.fileBase64;
        payload.evidence_name = evidenceForm.fileName;
      }

      await axios.post(`${baseURL}/api/progress`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await fetchProgress();
      setShowEvidenceModal(null);
      setEvidenceForm({ evidence: '', notes: '', status: 'Completed', fileBase64: null, fileName: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save progress');
    }
    setSavingProgress(false);
  };

  const runAnalysis = async () => {
    setRunning(true);
    setError('');
    try {
      // FIX: Added { orgId: user.org_id } to the POST body payload
      const res = await axios.post(`${baseURL}/api/inventory/analyze`, 
        { orgId: user.org_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalysis(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed');
    }
    setRunning(false);
  };

  // --- REUSABLE COMPONENTS FOR ACTION ITEMS ---
  const StatusButton = ({ itemKey, itemTitle, itemType, toolId }) => {
    const prog = progress[itemKey] || { status: 'Pending' };
    const status = prog.status || 'Pending';
    
    let btnColor = 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50';
    if (status === 'Completed') btnColor = 'bg-green-600 text-white border-green-700 hover:bg-green-700 shadow-inner';
    if (status === 'In Progress') btnColor = 'bg-blue-600 text-white border-blue-700 hover:bg-blue-700 shadow-inner';
    if (status === 'Waived') btnColor = 'bg-slate-500 text-white border-slate-600 hover:bg-slate-600 shadow-inner';

    return (
      <button 
        onClick={() => handleMarkDone(itemKey, itemTitle, itemType, toolId)}
        disabled={isImpersonating}
        className={`text-xs font-bold px-4 py-2 rounded-lg whitespace-nowrap transition shadow-sm border ${btnColor} flex items-center gap-2 ${isImpersonating ? 'opacity-50 cursor-not-allowed' : ''}`}>
        {status === 'Completed' ? '✓' : status === 'In Progress' ? '🔄' : status === 'Waived' ? '✕' : '✎'} {status}
      </button>
    );
  };

  const EvidenceDisplay = ({ itemKey }) => {
    const prog = progress[itemKey];
    if (!prog) return null;
    if (!prog.evidence && !prog.evidence_file_name && !prog.notes) return null;
    
    return (
      <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg px-5 py-4 text-sm text-slate-700 font-medium shadow-sm">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
           <span>📂</span> Documentation & Evidence Vault
        </div>
        <div className="space-y-2">
          {prog.evidence_file_name && (
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-md">
               📎 <strong>Secure Vault File:</strong> {prog.evidence_file_name}
            </div>
          )}
          {prog.evidence && <div className="text-blue-900 bg-blue-50 border border-blue-100 px-3 py-2 rounded-md">📝 <strong>Reference:</strong> {prog.evidence}</div>}
          {prog.notes && <div className="text-slate-500 text-xs italic px-1">"{prog.notes}"</div>}
        </div>
      </div>
    );
  };
  // -------------------------------------------

  const maturityPct   = analysis ? (analysis.maturity_score / 5) * 100 : 0;
  const maturityStyle = MATURITY_COLORS[analysis?.maturity_level] || MATURITY_COLORS['Initial'];

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-slate-500">Loading risk analysis...</div>
    </div>
  );

  return (
    <div className="p-8">

      

      {/* Evidence Modal */}
      {showEvidenceModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">Update Action Status</h3>
              <button onClick={() => setShowEvidenceModal(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <p className="text-sm text-slate-600 mb-4 bg-slate-50 rounded-lg p-3 border border-slate-200">{showEvidenceModal.itemTitle}</p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Status</label>
                <select className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                  value={evidenceForm.status}
                  onChange={e => setEvidenceForm({ ...evidenceForm, status: e.target.value })}>
                  <option value="Completed">✅ Completed</option>
                  <option value="In Progress">🔄 In Progress</option>
                  <option value="Pending">⏳ Pending</option>
                  <option value="Waived">✕ Waived (Risk Accepted)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Upload Secure Evidence <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer text-sm font-medium px-4 py-2 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 text-center transition-colors">
                    {evidenceForm.fileName ? `📎 ${evidenceForm.fileName}` : 'Choose File...'}
                    <input type="file" className="hidden" onChange={handleFileSelect} />
                  </label>
                  {evidenceForm.fileName && (
                    <button onClick={() => setEvidenceForm({...evidenceForm, fileBase64: null, fileName: ''})} className="text-red-500 text-sm hover:text-red-700">Clear</button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Reference Link / Text Evidence <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Jira Ticket #123, DPA signed 15 Mar"
                  value={evidenceForm.evidence}
                  onChange={e => setEvidenceForm({ ...evidenceForm, evidence: e.target.value })} />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  Internal Notes <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <textarea rows={2} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Any additional context for the board report..."
                  value={evidenceForm.notes}
                  onChange={e => setEvidenceForm({ ...evidenceForm, notes: e.target.value })} />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-700">
              📋 This action will be logged in your audit trail with a timestamp.
              Re-run Deep Analysis after completing actions to see your updated maturity score.
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowEvidenceModal(null)}
                className="flex-1 border border-slate-300 text-slate-600 py-2.5 rounded-lg text-sm font-bold hover:bg-slate-50">
                Cancel
              </button>
              <button disabled={savingProgress}
                onClick={() => {
                  setSavingProgress(true);
                  saveProgress(showEvidenceModal.itemKey, showEvidenceModal.itemTitle, showEvidenceModal.itemType, evidenceForm.status, showEvidenceModal.toolId);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-lg text-sm disabled:opacity-50">
                {savingProgress ? 'Saving...' : 'Update & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Deep Risk Analysis</h2>
          <p className="text-slate-500 mt-1">Cross-referencing {inventory.length} AI tools against global regulations.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/progress')}
            className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold px-4 py-2.5 rounded-lg text-sm transition shadow-sm">
            ✅ View Progress Tracker
          </button>

          <button 
            onClick={runAnalysis} 
            disabled={running || inventory.length === 0 || isImpersonating}
            className={`font-bold px-6 py-2.5 rounded-lg text-sm transition shadow-sm flex items-center gap-2 ${
              isImpersonating 
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'
            }`}
          >
            {isImpersonating ? '🔒 Impersonation Read-Only' : (running ? '🔄 Analysing...' : '🔍 Run Deep Analysis')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm shadow-sm">{error}</div>
      )}

      {/* No analysis yet */}
      {!analysis && !running && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-16 text-center">
          <div className="text-5xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">No Deep Analysis Yet</h2>
          <p className="text-slate-500 text-sm mb-6 max-w-md mx-auto">
            Click "Run Deep Analysis" to get your organisation's full AI governance risk profile —
            including cross-tool risks, financial exposure, and a prioritised action plan.
          </p>
          <button 
            onClick={runAnalysis} 
            disabled={inventory.length === 0 || isImpersonating}
            className={`font-semibold px-8 py-3 rounded-lg transition shadow-sm disabled:opacity-50 ${
              isImpersonating ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}>
            {isImpersonating ? '🔒 Read-Only' : '🔍 Run Deep Analysis Now'}
          </button>
          {inventory.length === 0 && (
            <p className="text-slate-400 text-xs mt-4">Add AI tools to your inventory first</p>
          )}
        </div>
      )}

      {running && (
        <div className="bg-white rounded-3xl shadow-xl p-16 text-center relative overflow-hidden border border-slate-200">
          <div className="absolute inset-0 opacity-40 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:14px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            <div className="relative w-28 h-28 mb-10 flex justify-center items-center">
              <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin shadow-[0_0_15px_rgba(37,99,235,0.2)]"></div>
              <div className="absolute inset-4 border-4 border-purple-200 rounded-full border-b-transparent animate-[spin_1.5s_linear_infinite_reverse]"></div>
              
              <div className="text-blue-600 animate-pulse drop-shadow-sm flex items-center justify-center">
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
            </div>
            
            <h2 className="text-3xl font-extrabold text-slate-800 mb-4 tracking-tight">
              Risk Analysis Progress
            </h2>
            
            <div className="max-w-md mx-auto space-y-4 text-left w-full mb-10 bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <div className="w-5 h-5 rounded-full border-2 border-blue-600 border-t-transparent animate-spin flex-shrink-0"></div>
                <span className="animate-pulse">Mapping AI inventory and data exposure vectors...</span>
              </div>
              <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <div className="w-5 h-5 rounded-full border-2 border-purple-500 border-t-transparent animate-spin flex-shrink-0" style={{animationDelay: '0.2s'}}></div>
                <span className="animate-pulse" style={{animationDelay: '0.2s'}}>Evaluating regulatory compliance and security posture...</span>
              </div>
              <div className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                <div className="w-5 h-5 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin flex-shrink-0" style={{animationDelay: '0.4s'}}></div>
                <span className="animate-pulse" style={{animationDelay: '0.4s'}}>Quantifying financial risk and generating action plan...</span>
              </div>
            </div>

            <div className="w-full max-w-sm bg-slate-100 rounded-full h-1.5 overflow-hidden relative shadow-inner">
              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-400 w-1/2 rounded-full animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]"></div>
              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 w-full rounded-full animate-pulse opacity-80"></div>
            </div>
            
            <p className="text-slate-500 text-xs mt-6 font-medium tracking-widest uppercase">
              Estimated time remaining: 15-30s
            </p>
          </div>
        </div>
      )}

      {analysis && !running && (
        <>
          {/* Maturity Hero */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-6">
                <div className={`w-20 h-20 rounded-2xl ${maturityStyle.bg} flex flex-col items-center justify-center border-2 border-current ${maturityStyle.text}`}>
                  <span className={`text-3xl font-bold ${maturityStyle.text}`}>{analysis.maturity_score?.toFixed(1)}</span>
                  <span className={`text-xs font-medium ${maturityStyle.text}`}>/5.0</span>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">AI Governance Maturity</div>

                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-xs text-slate-500 mb-2 inline-block">
                    🌍 Analysis calibrated for <strong>{user?.country || 'your jurisdiction'}</strong> ·{' '}
                    <span className="text-blue-600 font-medium">{user?.vertical}</span>
                  </div>

                  <div className={`text-2xl font-bold ${maturityStyle.text}`}>{analysis.maturity_level}</div>
                  <div className="w-48 bg-slate-100 rounded-full h-2 mt-2">
                    <div className={`h-2 rounded-full ${maturityStyle.bar} transition-all`} style={{ width: `${maturityPct}%` }} />
                  </div>
                </div>
              </div>
              {analysis.combined_exposure && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-5 min-w-64">
                  <div className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-1">Combined Financial Exposure</div>
                  <div className="text-2xl font-bold text-red-700">
                    {formatCurrency(analysis.combined_exposure.total_min, user?.country)} – {formatCurrency(analysis.combined_exposure.total_max, user?.country)}
                  </div>
                  <div className="text-xs font-medium text-red-600 mt-1">{analysis.combined_exposure.key_driver}</div>
                </div>
              )}
            </div>
            <div className="flex justify-between mt-4 px-1">
              {['Initial','Developing','Defined','Managed','Optimised'].map(l => (
                <span key={l} className={`text-xs ${analysis.maturity_level === l ? `font-bold ${maturityStyle.text}` : 'text-slate-400'}`}>
                  {l}
                </span>
              ))}
            </div>
            
            {analysis.maturity_reasoning && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-600">
                  <strong className="text-slate-800">AI Assessment:</strong> {analysis.maturity_reasoning}
                </p>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-slate-200 shadow-sm rounded-xl p-1 mb-6 w-fit">
            {[
              { id: 'overview',  label: '📊 Overview'         },
              { id: 'tools',     label: '🔬 Tool Scorecards'  },
              { id: 'crossrisk', label: '⚠️ Cross-Tool Risks' },
              { id: 'actions',   label: '✅ Action Plan'       },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50 shadow-none'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <h3 className="font-bold text-lg text-slate-800 mb-4">🚨 Critical Governance Gaps</h3>
                
                {(!analysis.cross_tool_risks || analysis.cross_tool_risks.length === 0) ? (
                   <div className="text-center py-10 bg-blue-50 border border-blue-200 rounded-xl">
                     <span className="text-4xl">🔍</span>
                     <h4 className="text-blue-800 font-bold mt-3">No Compounding Risks Detected</h4>
                     <p className="text-blue-700 text-sm mt-1">You currently have {inventory.length} active AI tool(s). Cross-tool vulnerabilities will appear here when multiple tools interact and compound your risk profile.</p>
                   </div>
                ) : (
                  <div className="space-y-4">
                    {analysis.cross_tool_risks.map((risk, i) => {
                      const isOptimised = analysis.maturity_level === 'Optimised' || analysis.maturity_level === 'Optimized';
                      const itemKey = `cross_risk_${i}`;
                      
                      return (
                        <div key={i} className={`border rounded-xl p-5 transition-all ${
                          isOptimised ? 'border-green-200 bg-green-50/50' :
                          risk.severity === 'Critical' ? 'border-red-200 bg-red-50' :
                          risk.severity === 'High'     ? 'border-orange-200 bg-orange-50' :
                                                         'border-yellow-200 bg-yellow-50'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {isOptimised ? (
                                <span className="text-xs font-bold px-2.5 py-1 rounded-full border bg-green-100 text-green-700 border-green-200">
                                  ✅ Risk Mitigated
                                </span>
                              ) : (
                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${URGENCY_COLORS[risk.severity]}`}>
                                  {risk.severity} Risk
                                </span>
                              )}
                              <span className={`font-bold text-base ${isOptimised ? 'text-slate-600' : 'text-slate-800'}`}>
                                {risk.risk_title}
                              </span>
                            </div>
                          </div>
                          <p className={`text-sm mb-3 ${isOptimised ? 'text-slate-500' : 'text-slate-700'}`}>{risk.description}</p>
                          <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span className="text-xs font-medium text-slate-500">Tools involved:</span>
                            {(risk.tools_involved || []).map(t => (
                              <span key={t} className="bg-white border border-slate-200 shadow-sm text-slate-700 text-xs px-2.5 py-1 rounded font-medium">{t}</span>
                            ))}
                          </div>
                          {!isOptimised && (
                            <div className="mt-3 bg-white border border-slate-200 rounded-xl px-4 py-4 shadow-sm">
                              <div className="flex justify-between items-start gap-4">
                                <div>
                                  <span className="text-xs font-bold text-blue-700 uppercase tracking-wider block mb-1">Immediate Action</span>
                                  <span className="font-bold text-slate-800">{risk.immediate_action}</span>
                                </div>
                                <StatusButton itemKey={itemKey} itemTitle={risk.immediate_action} itemType="cross_risk" />
                              </div>
                              <EvidenceDisplay itemKey={itemKey} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                  <h3 className="font-bold text-lg text-slate-800 mb-4">💰 Financial Exposure</h3>
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="text-xs text-red-600 font-bold mb-1 uppercase tracking-wider">Regulatory Fines</div>
                      <div className="text-xl font-bold text-red-700">
                        {formatCurrency(analysis.combined_exposure?.total_min ? analysis.combined_exposure.total_min * 0.3 : 50000, user?.country)} –{' '}
                        {formatCurrency(analysis.combined_exposure?.total_max ? analysis.combined_exposure.total_max * 0.4 : 400000, user?.country)}
                      </div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                      <div className="text-xs text-orange-600 font-bold mb-1 uppercase tracking-wider">Breach + Recovery Costs</div>
                      <div className="text-xl font-bold text-orange-700">
                        {formatCurrency(analysis.combined_exposure?.total_min, user?.country)} –{' '}
                        {formatCurrency(analysis.combined_exposure?.total_max, user?.country)}
                      </div>
                    </div>
                    <div className="text-sm text-slate-500 font-medium leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                      {analysis.combined_exposure?.comparison}
                    </div>
                  </div>
                </div>
                
                {analysis.priority_actions && analysis.priority_actions.length > 0 ? (
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700 shadow-sm p-6 text-white">
                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><span>⭐</span> Top Priority</h3>
                    <div className="bg-slate-800/50 border border-slate-600 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">#1 Critical</span>
                        <span className="text-xs font-medium text-slate-300">{analysis.priority_actions[0].timeline}</span>
                      </div>
                      <p className="text-base font-bold text-white mb-2">{analysis.priority_actions[0].action}</p>
                      <p className="text-xs font-medium text-slate-400">Owner: {analysis.priority_actions[0].owner}</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-green-900 to-green-800 rounded-2xl border border-green-700 shadow-sm p-6 text-white text-center">
                    <h3 className="font-bold text-lg mb-2 flex justify-center items-center gap-2"><span>⭐</span> All Clear</h3>
                    <p className="text-sm text-green-100">No urgent priority actions required. Governance is up to date.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TOOL SCORECARDS ── */}
          {activeTab === 'tools' && (
            <div className="space-y-4">
              {inventory.filter(t => t.risk_dimensions).map(tool => (
                <div key={tool.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all">
                  <button className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50 transition"
                    onClick={() => setExpandedTool(expandedTool === tool.id ? null : tool.id)}>
                    <div className="flex items-center gap-5">
                      <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center border ${
                        tool.risk_score >= 8 ? 'bg-red-50 border-red-200' : tool.risk_score >= 6 ? 'bg-orange-50 border-orange-200' :
                        tool.risk_score >= 4 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                        <span className={`text-xl font-bold ${
                          tool.risk_score >= 8 ? 'text-red-700' : tool.risk_score >= 6 ? 'text-orange-700' :
                          tool.risk_score >= 4 ? 'text-yellow-700' : 'text-green-700'}`}>
                          {tool.risk_score}
                        </span>
                        <span className="text-[10px] font-medium text-slate-500 uppercase">Score</span>
                      </div>
                      <div className="text-left">
                        <div className="text-lg font-bold text-slate-900">{tool.tool_name}</div>
                        <div className="text-sm font-medium text-slate-500">{tool.vendor}</div>
                      </div>
                      <span className={`text-xs px-3 py-1.5 rounded-full border font-bold shadow-sm ${
                        tool.eu_ai_act_risk === 'High' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                        tool.eu_ai_act_risk === 'Unacceptable' ? 'bg-red-100 text-red-700 border-red-200' :
                        'bg-green-100 text-green-700 border-green-200'}`}>
                        {tool.eu_ai_act_risk} Risk
                      </span>
                    </div>
                    <span className="text-slate-400 bg-slate-100 w-8 h-8 rounded-full flex items-center justify-center">
                      {expandedTool === tool.id ? '▲' : '▼'}
                    </span>
                  </button>

                  {expandedTool === tool.id && (
                    <div className="px-6 pb-6 border-t border-slate-100 bg-slate-50/50">
                      {tool.risk_reasoning && (
                        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5 mt-5 mb-6">
                          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Risk Reasoning</div>
                          <p className="text-sm text-slate-700 leading-relaxed">{tool.risk_reasoning}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        {Object.entries(tool.risk_dimensions || {}).map(([key, dim]) => (
                          <div key={key} className="bg-white border border-slate-200 shadow-sm rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-bold text-slate-700">{DIM_LABELS[key] || key}</span>
                              <span className={`text-sm font-bold px-2.5 py-0.5 rounded border ${
                                dim.score >= 8 ? 'text-red-700 bg-red-50 border-red-200' : dim.score >= 6 ? 'text-orange-700 bg-orange-50 border-orange-200' :
                                dim.score >= 4 ? 'text-yellow-700 bg-yellow-50 border-yellow-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
                                {dim.score}/10
                              </span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2 mb-3">
                              <div className={`h-2 rounded-full ${
                                dim.score >= 8 ? 'bg-red-500' : dim.score >= 6 ? 'bg-orange-500' :
                                dim.score >= 4 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${dim.score * 10}%` }} />
                            </div>
                            <p className="text-xs font-medium text-slate-500 leading-relaxed">{dim.reasoning}</p>
                          </div>
                        ))}
                      </div>

                      {tool.compliance_gaps && tool.compliance_gaps.length > 0 && (
                        <div className="mb-6 mt-4">
                          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Compliance Gaps Action Items</div>
                          <div className="space-y-3">
                            {tool.compliance_gaps.map((gap, i) => {
                              const itemKey = `gap_${tool.id}_${i}`;
                              const isDone = progress[itemKey]?.status === 'Completed';
                              return (
                                <div key={i} className={`flex flex-col gap-2 rounded-xl px-4 py-4 shadow-sm border ${isDone ? 'bg-green-50/50 border-green-200' : 'bg-white border-slate-200'}`}>
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1 mt-1">
                                      <span className="text-lg">{isDone ? '✅' : '⚠'}</span>
                                      <span className={`text-sm font-medium ${isDone ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{gap}</span>
                                    </div>
                                    <div className="flex-shrink-0">
                                      <StatusButton itemKey={itemKey} itemTitle={gap} itemType="compliance_gap" toolId={tool.id} />
                                    </div>
                                  </div>
                                  <EvidenceDisplay itemKey={itemKey} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Financial Exposure */}
                      {tool.financial_exposure && (
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 shadow-sm">
                          <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Financial Exposure — This Tool</div>
                          <div className="text-xl font-bold text-amber-900 mb-1">
                            {formatCurrency(tool.financial_exposure.gdpr_min, user?.country)} –{' '}
                            {formatCurrency(tool.financial_exposure.breach_cost_max, user?.country)}
                          </div>
                          <p className="text-sm font-medium text-amber-800">{tool.financial_exposure.exposure_reasoning}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── CROSS-TOOL RISKS ── */}
          {activeTab === 'crossrisk' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 shadow-sm flex gap-4 items-start">
                <div className="text-3xl">⚠️</div>
                <p className="text-sm font-medium text-blue-900 leading-relaxed">
                  <strong>Cross-tool risks</strong> are dangers that emerge from using multiple AI tools together —
                  invisible when analysing each tool in isolation. These are the compounding risks most organisations miss entirely until a breach occurs.
                </p>
              </div>
              
              {(!analysis.cross_tool_risks || analysis.cross_tool_risks.length === 0) ? (
                 <div className="text-center py-10 bg-blue-50 border border-blue-200 rounded-xl"><h4 className="text-blue-800 font-bold text-lg">No Critical Cross-Tool Risks</h4></div>
              ) : (
                analysis.cross_tool_risks.map((risk, i) => {
                  const itemKey = `cross_risk_${i}`;
                  const isOptimised = analysis.maturity_level === 'Optimised' || analysis.maturity_level === 'Optimized';
                  return (
                    <div key={i} className={`bg-white rounded-2xl border-2 shadow-sm p-6 ${
                      isOptimised ? 'border-green-200 opacity-80' :
                      risk.severity === 'Critical' ? 'border-red-300' :
                      risk.severity === 'High'     ? 'border-orange-300' : 'border-yellow-300'}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full border shadow-sm ${URGENCY_COLORS[risk.severity]}`}>{risk.severity} Severity</span>
                        <span className="font-bold text-lg text-slate-800">{risk.risk_title}</span>
                      </div>
                      <p className="text-base mb-5 leading-relaxed text-slate-600">{risk.description}</p>
                      
                      {!isOptimised && (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 mt-5">
                          <div className="flex justify-between items-start gap-4">
                            <div>
                              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">Immediate Action Required</span>
                              <p className="text-sm font-bold text-slate-800">{risk.immediate_action}</p>
                            </div>
                            <StatusButton itemKey={itemKey} itemTitle={risk.immediate_action} itemType="cross_risk" />
                          </div>
                          <EvidenceDisplay itemKey={itemKey} />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── ACTION PLAN ── */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center gap-5 mb-6">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center text-2xl shadow-sm">📋</div>
                <div>
                  <div className="text-lg font-bold text-slate-800">Prioritised Governance Action Plan</div>
                  <div className="text-sm font-medium text-slate-500 mt-1">
                    Actions ranked by urgency and impact. Tackle in order — each completed action structurally reduces your financial exposure.
                  </div>
                </div>
              </div>

              {(!analysis.priority_actions || analysis.priority_actions.length === 0) ? (
                 <div className="text-center py-16 bg-white border border-slate-200 shadow-sm rounded-2xl"><h4 className="text-slate-800 font-bold text-xl">Governance Mastered</h4></div>
              ) : (
                analysis.priority_actions.map((action, i) => {
                  const itemKey = `action_${action.rank || i + 1}`;
                  const isDone = progress[itemKey]?.status === 'Completed';
                  const isInProgress = progress[itemKey]?.status === 'In Progress';
                  
                  return (
                    <div key={i} className={`rounded-2xl border shadow-sm p-6 transition-all ${
                      isDone ? 'bg-green-50/30 border-green-200' :
                      isInProgress ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}>
                      <div className="flex items-start gap-5">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-white text-lg shadow-sm ${
                          isDone ? 'bg-green-500' :
                          i === 0 ? 'bg-red-600' : i === 1 ? 'bg-orange-500' :
                          i === 2 ? 'bg-yellow-500' : 'bg-blue-500'}`}>
                          {isDone ? '✓' : `#${action.rank || i + 1}`}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap mb-3">
                            <span className={`text-lg font-bold ${isDone ? 'text-slate-500 line-through' : 'text-slate-800'}`}>{action.action}</span>
                            <span className={`text-xs px-2.5 py-1 rounded-full border font-bold shadow-sm ${URGENCY_COLORS[action.urgency]}`}>
                              {action.urgency} Urgency
                            </span>
                            <span className={`text-xs px-2.5 py-1 rounded-full font-bold border shadow-sm ${EFFORT_COLORS[action.effort]}`}>
                              {action.effort} effort
                            </span>
                          </div>
                          
                          {action.why_first && !isDone && (
                            <p className="text-sm font-medium text-slate-600 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100">{action.why_first}</p>
                          )}

                          <div className="flex items-center justify-between flex-wrap gap-4 pt-4 border-t border-slate-100/50 mt-4">
                            <div className="flex items-center gap-6 text-sm font-medium text-slate-500">
                              <span className="flex items-center gap-1">⏱ <strong className="text-slate-700">{action.timeline}</strong></span>
                              <span className="flex items-center gap-1">👤 <strong className="text-slate-700">{action.owner}</strong></span>
                              {action.estimated_risk_reduction && (
                                <span className="text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100">
                                  ↓ {action.estimated_risk_reduction} risk reduction
                                </span>
                              )}
                            </div>
                            <StatusButton itemKey={itemKey} itemTitle={action.action} itemType="action_plan" />
                          </div>
                          <EvidenceDisplay itemKey={itemKey} />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}